import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/useEntity';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { fmt } from '../lib/formatters';

/**
 * Sección "Ventas de APPROX a <cliente>" de la ficha de producto: el precio
 * unitario al que APPROX vendió este producto en cada albarán, según el
 * histórico exportado del ERP de APPROX (importador /<cliente>/importador-
 * ventas-approx). Distinto del "Histórico de precio de venta" de arriba, que
 * sale de los pedidos registrados en esta app.
 *
 * No se pinta nada si el cliente no tiene ningún histórico importado (GAME
 * hoy): la sección solo aparece donde hay datos que consultar. Las líneas a
 * 0,00 no existen aquí (no se importan); los abonos van marcados y fuera de
 * los KPIs de precio. Multi-cliente: la ruta se deriva de clienteCodigo.
 */

type Venta = {
  id: string; fecha: string; albaran: string | null; codigo_texto: string; descripcion: string | null;
  cantidad: number; precio_unitario: number; dto_pct: number; importe: number; es_abono: boolean; fichero: string;
};
type Respuesta = {
  lineas: Venta[];
  kpis: {
    ultimo_precio: number | null;
    ultima_venta: { fecha: string; uds: number } | null;
    precio_min: number | null;
    precio_max: number | null;
    n_ventas: number;
    n_abonos: number;
    uds_vendidas: number;
    importe_vendido: number;
  };
  cobertura: { desde: string | null; hasta: string | null; fecha_informe: string | null } | null;
};

const FILAS_PLEGADO = 12;

export function VentasApproxFicha({ productoId, clienteCodigo, forzar = false }: { productoId: string; clienteCodigo: string; /** Pinta la sección aunque el cliente no tenga histórico (pestaña propia de la ficha). */ forzar?: boolean }) {
  const [verTodas, setVerTodas] = useState(false);
  const base = `/${clienteCodigo.toLowerCase()}`;
  const q = useQuery({
    queryKey: ['ventas-approx', productoId],
    queryFn: () => api.get<Respuesta>(`/productos/${productoId}/ventas-approx`),
  });

  if (q.isLoading) return null;
  if (q.error) {
    return (
      <div className="mb-6">
        <Card title={`Ventas de APPROX a ${clienteCodigo}`}>
          <p role="alert" className="text-sm text-rose-700">No se pudo cargar el histórico de ventas de APPROX.</p>
          <Button variant="secondary" size="sm" className="mt-2" onClick={() => q.refetch()}>Reintentar</Button>
        </Card>
      </div>
    );
  }
  const d = q.data!;
  // Sin histórico importado para este cliente: la sección no existe (salvo en su pestaña, donde se explica).
  if (!d.cobertura && !forzar) return null;
  if (!d.cobertura) {
    return (
      <div className="mb-6">
        <Card title={`Ventas de APPROX a ${clienteCodigo}`} description="Precio unitario al que APPROX vendió el producto en cada albarán, según el histórico exportado del ERP de APPROX.">
          <p className="text-sm text-slate-500">No hay ningún histórico de ventas de APPROX importado para {clienteCodigo}. Se carga desde <Link to={`${base}/importador-ventas-approx`} className="underline">Importador ventas APPROX</Link>.</p>
        </Card>
      </div>
    );
  }
  const k = d.kpis;
  const variosCodigos = new Set(d.lineas.map((l) => l.codigo_texto)).size > 1;
  const filas = verTodas ? d.lineas : d.lineas.slice(0, FILAS_PLEGADO);
  const tope = d.cobertura.fecha_informe ?? d.cobertura.hasta;

  return (
    <div className="mb-6">
      <Card
        title={`Ventas de APPROX a ${clienteCodigo}`}
        description={`Precio unitario al que APPROX vendió este producto en cada albarán, según el histórico del ERP de APPROX. Datos hasta el ${fmt.date(tope)}${d.cobertura.desde ? ` (desde el ${fmt.date(d.cobertura.desde)})` : ''}. Las líneas a 0,00 € no se importan; los abonos no cuentan para el precio.`}
        actions={<Link to={`${base}/importaciones`} className="text-xs text-slate-600 underline">Ficheros importados</Link>}
      >
        {k.n_ventas === 0 && k.n_abonos === 0 ? (
          <p className="text-sm text-slate-500">
            Sin ventas de APPROX a {clienteCodigo} para este producto en el histórico cargado (hasta el {fmt.date(tope)}).
            {' '}Si el código del ERP es distinto del SKU, enlázalo en «Ficheros importados → Pendientes de enlazar».
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 text-sm lg:grid-cols-4">
              <Kpi
                label="Último precio de venta"
                valor={k.ultimo_precio != null ? fmt.eur(k.ultimo_precio) : '—'}
                nota={k.ultima_venta ? `${fmt.date(k.ultima_venta.fecha)} · ${fmt.int(k.ultima_venta.uds)} uds` : undefined}
              />
              <Kpi
                label="Rango de precio"
                valor={k.precio_min != null && k.precio_max != null ? (k.precio_min === k.precio_max ? fmt.eur(k.precio_min) : `${fmt.eur(k.precio_min)} – ${fmt.eur(k.precio_max)}`) : '—'}
                nota={k.n_ventas === 1 ? 'una sola venta' : `en ${fmt.int(k.n_ventas)} ventas`}
              />
              <Kpi label="Unidades vendidas" valor={fmt.int(k.uds_vendidas)} nota={k.n_abonos ? `${fmt.int(k.n_abonos)} abono${k.n_abonos === 1 ? '' : 's'} aparte` : 'sin abonos'} />
              <Kpi label="Importe vendido" valor={fmt.eur(k.importe_vendido)} nota="sin abonos" />
            </div>

            <div className="mt-4 overflow-x-auto rounded-md border border-slate-200">
              <table className="min-w-full divide-y divide-slate-200 text-xs">
                <thead className="bg-slate-50">
                  <tr><Th>Fecha</Th><Th>Albarán</Th>{variosCodigos && <Th>Código ERP</Th>}<Th right>Uds</Th><Th right>Precio/ud</Th>{d.lineas.some((l) => l.dto_pct !== 0) && <Th right>Dto</Th>}<Th right>Importe</Th><Th>Tipo</Th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filas.map((l) => (
                    <tr key={l.id} className={l.es_abono ? 'text-slate-500' : undefined}>
                      <Td>{fmt.date(l.fecha)}</Td>
                      <Td>{l.albaran ?? <span className="text-slate-400">—</span>}</Td>
                      {variosCodigos && <Td><span className="font-mono">{l.codigo_texto}</span></Td>}
                      <Td right>{fmt.int(l.cantidad)}</Td>
                      <Td right><span className={l.es_abono ? '' : 'font-semibold'}>{fmt.eur(l.precio_unitario)}</span></Td>
                      {d.lineas.some((x) => x.dto_pct !== 0) && <Td right>{l.dto_pct ? fmt.pct(l.dto_pct) : '—'}</Td>}
                      <Td right>{fmt.eur(l.importe)}</Td>
                      <Td>{l.es_abono ? <Badge color="#f59e0b">Abono</Badge> : <Badge color="#10b981">Venta</Badge>}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {d.lineas.length > FILAS_PLEGADO && (
              <button type="button" onClick={() => setVerTodas(!verTodas)} className="mt-2 cursor-pointer text-xs text-slate-700 underline">
                {verTodas ? `Mostrar solo las ${FILAS_PLEGADO} más recientes` : `Ver las ${fmt.int(d.lineas.length)} líneas`}
              </button>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

function Kpi({ label, valor, nota }: { label: string; valor: React.ReactNode; nota?: string }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-lg font-semibold tabular-nums text-slate-900">{valor}</div>
      {nota && <div className="text-[11px] text-slate-500">{nota}</div>}
    </div>
  );
}
function Th({ children, right }: { children?: React.ReactNode; right?: boolean }) {
  return <th scope="col" className={`px-3 py-2 font-semibold text-slate-600 ${right ? 'text-right' : 'text-left'}`}>{children}</th>;
}
function Td({ children, right }: { children?: React.ReactNode; right?: boolean }) {
  return <td className={`px-3 py-2 align-top ${right ? 'text-right tabular-nums' : 'text-left'}`}>{children}</td>;
}
